import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, Loader2, RefreshCw, ExternalLink,
  Paperclip, Mail, AlertTriangle, Banknote, Receipt, ShoppingCart,
} from "lucide-react";

const STATUS_COLORS = {
  DRAFT: "bg-zinc-100 text-zinc-700 border-zinc-200",
  SUBMITTED: "bg-blue-50 text-blue-800 border-blue-200",
  AUTHORISED: "bg-amber-50 text-amber-800 border-amber-200",
  PAID: "bg-emerald-100 text-emerald-800 border-emerald-200",
  VOIDED: "bg-zinc-100 text-zinc-500 border-zinc-200",
  DELETED: "bg-red-50 text-red-700 border-red-200",
};

/**
 * Xero invoice / bill detail page. Backed by the single-invoice
 * endpoint that surfaces full line items, applied payments + credits,
 * tracking categories, attachments flag and history. Linked from
 * the row click on `XeroInvoicesPage`.
 */
export default function XeroInvoiceDetailPage() {
  const { tenantId, invoiceId } = useParams();
  const [tenant, setTenant] = useState(null);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadTenant = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/xero/tenants");
      setTenant((data || []).find((t) => t.tenant_id === tenantId)
        || { tenant_id: tenantId, tenant_name: tenantId });
    } catch (e) {
      // Non-fatal — header still renders with the tenant_id as a fallback.
      logger.warn("[Xero] tenant lookup failed:", e?.response?.data || e);
    }
  }, [tenantId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/integrations/xero/tenants/${tenantId}/invoices/${invoiceId}`);
      setDoc(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invoice");
    } finally { setLoading(false); }
  }, [tenantId, invoiceId]);

  useEffect(() => { loadTenant(); }, [loadTenant]);
  useEffect(() => { load(); }, [load]);

  const isBill = doc?.type === "ACCPAY";
  const TypeIcon = isBill ? Receipt : ShoppingCart;

  return (
    <div className="p-6 max-w-6xl" data-testid="xero-invoice-detail-page">
      <Link to={`/integrations/xero/tenants/${tenantId}/invoices`}
        className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900 mb-3"
        data-testid="xero-invoice-detail-back">
        <ArrowLeft className="h-3 w-3" /> Back to {isBill ? "bills" : "invoices"}
      </Link>

      <div className="flex items-start gap-3 mb-5 flex-wrap">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-[#13B5EA]/10">
          <TypeIcon className="h-6 w-6 text-[#13B5EA]" />
        </div>
        <div className="flex-1 min-w-[280px]">
          <div className="label-overline">
            XERO · {tenant?.tenant_name || "…"} · {isBill ? "SUPPLIER BILL" : "SALES INVOICE"}
          </div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            {doc?.invoice_number || (loading ? "Loading…" : "—")}
          </h1>
          {doc?.reference && (
            <div className="text-xs text-zinc-500 font-mono mt-0.5">
              Ref: {doc.reference}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {doc?.status && (
            <Badge variant="outline"
              data-testid="xero-invoice-detail-status"
              className={`rounded-sm text-[10px] font-display font-bold uppercase tracking-wider ${STATUS_COLORS[doc.status] || ""}`}>
              {doc.status}
            </Badge>
          )}
          <Button size="sm" variant="outline" className="rounded-sm"
            onClick={load} data-testid="xero-invoice-detail-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {loading && !doc ? (
        <Card className="rounded-sm border-zinc-200"><CardContent className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></CardContent></Card>
      ) : !doc ? null : (
        <>
          {/* Top summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4"
            data-testid="xero-invoice-detail-summary">
            <SummaryBlock label={isBill ? "Supplier" : "Customer"}
              value={doc.contact?.name || "—"}
              sub={doc.contact?.email || ""}
              testid="xero-invoice-detail-contact" />
            <SummaryBlock label="Issue date" value={fmtDate(doc.date)} />
            <SummaryBlock label="Due date" value={fmtDate(doc.due_date)}
              accent={isOverdue(doc) ? "text-red-700" : ""}
              icon={isOverdue(doc) ? <AlertTriangle className="h-3 w-3 inline ml-1" /> : null} />
            <SummaryBlock label="Total"
              value={`${fmtMoney(doc.total)} ${doc.currency_code || ""}`}
              sub={`Tax ${fmtMoney(doc.total_tax)}`} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <SummaryBlock label="Subtotal" value={fmtMoney(doc.sub_total)} />
            <SummaryBlock label="Tax" value={fmtMoney(doc.total_tax)} />
            <SummaryBlock label="Amount paid" value={fmtMoney(doc.amount_paid)}
              accent={(doc.amount_paid || 0) > 0 ? "text-emerald-700" : ""} />
            <SummaryBlock label="Amount due" value={fmtMoney(doc.amount_due)}
              accent={(doc.amount_due || 0) > 0 ? "text-amber-700" : ""} />
          </div>

          {/* Metadata strip */}
          <div className="flex flex-wrap gap-2 mb-6 text-[10px] uppercase tracking-[0.15em] font-bold">
            {doc.has_attachments && (
              <Badge className="rounded-sm bg-violet-50 text-violet-800 border-violet-200">
                <Paperclip className="h-3 w-3 mr-1" /> Has attachments
              </Badge>
            )}
            {doc.sent_to_contact && (
              <Badge className="rounded-sm bg-blue-50 text-blue-800 border-blue-200">
                <Mail className="h-3 w-3 mr-1" /> Sent to contact
              </Badge>
            )}
            {doc.fully_paid_on_date && (
              <Badge className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">
                Fully paid {fmtDate(doc.fully_paid_on_date)}
              </Badge>
            )}
            {doc.line_amount_types && (
              <Badge variant="outline" className="rounded-sm">
                Amounts are {String(doc.line_amount_types).replace("Inclusive", "tax-inclusive").replace("Exclusive", "tax-exclusive").replace("NoTax", "no tax")}
              </Badge>
            )}
            {doc.url && (
              <a href={doc.url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-[#13B5EA] text-[#13B5EA] hover:bg-[#13B5EA]/5"
                data-testid="xero-invoice-detail-xero-link">
                Open in Xero <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          {/* Line items */}
          <SectionHeading icon={FileText}
            title={`Line items · ${doc.line_items?.length || 0}`} />
          <Card className="rounded-sm border-zinc-200 mb-6"
            data-testid="xero-invoice-detail-lineitems">
            <CardContent className="p-0">
              <div className="grid grid-cols-[1fr_70px_100px_100px_110px_120px] gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
                <span>Description</span>
                <span className="text-right">Qty</span>
                <span className="text-right">Unit</span>
                <span>Account</span>
                <span>Tax</span>
                <span className="text-right">Line total</span>
              </div>
              {(doc.line_items || []).length === 0 ? (
                <div className="p-6 text-center text-sm italic text-zinc-500">
                  No line items on this {isBill ? "bill" : "invoice"}.
                </div>
              ) : (
                doc.line_items.map((li, idx) => (
                  <div key={li.line_item_id || `li-${idx}`}
                    className="grid grid-cols-[1fr_70px_100px_100px_110px_120px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-start"
                    data-testid={`xero-invoice-line-${idx}`}>
                    <div className="min-w-0">
                      <div className="whitespace-pre-wrap break-words">
                        {li.description || <span className="italic text-zinc-400">—</span>}
                      </div>
                      {li.item_code && (
                        <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                          Item: {li.item_code}
                        </div>
                      )}
                      {(li.tracking || []).length > 0 && (
                        <div className="text-[10px] font-mono text-zinc-500 mt-0.5 flex flex-wrap gap-1">
                          {li.tracking.map((t) => (
                            <span key={`${t.name}:${t.option}`}
                              className="px-1 py-0.5 bg-zinc-100 rounded-sm">
                              {t.name}: {t.option}
                            </span>
                          ))}
                        </div>
                      )}
                      {(li.discount_rate || li.discount_amount) && (
                        <div className="text-[10px] text-amber-700 mt-0.5">
                          Discount {li.discount_rate ? `${li.discount_rate}%` : fmtMoney(li.discount_amount)}
                        </div>
                      )}
                    </div>
                    <span className="text-right font-mono text-[11px]">
                      {li.quantity != null ? Number(li.quantity).toLocaleString(undefined, { maximumFractionDigits: 4 }) : "—"}
                    </span>
                    <span className="text-right font-mono text-[11px]">
                      {fmtMoney(li.unit_amount)}
                    </span>
                    <span className="font-mono text-[11px] text-zinc-600">
                      {li.account_code || "—"}
                    </span>
                    <span className="font-mono text-[10px] text-zinc-600 truncate" title={li.tax_type}>
                      {li.tax_type || "—"}
                      {li.tax_amount != null && (
                        <div className="text-zinc-500">{fmtMoney(li.tax_amount)}</div>
                      )}
                    </span>
                    <span className="text-right font-mono font-semibold">
                      {fmtMoney(li.line_amount)}
                    </span>
                  </div>
                ))
              )}
              <div className="grid grid-cols-[1fr_70px_100px_100px_110px_120px] gap-2 px-3 py-2 bg-zinc-50 border-t border-zinc-200 text-[11px] font-mono">
                <span className="col-span-5 text-right font-display font-bold uppercase text-zinc-700 tracking-[0.15em]">Subtotal</span>
                <span className="text-right font-semibold">{fmtMoney(doc.sub_total)}</span>
                <span className="col-span-5 text-right font-display font-bold uppercase text-zinc-700 tracking-[0.15em]">Tax</span>
                <span className="text-right font-semibold">{fmtMoney(doc.total_tax)}</span>
                <span className="col-span-5 text-right font-display font-bold uppercase text-zinc-900 tracking-[0.15em]">Total</span>
                <span className="text-right font-bold">{fmtMoney(doc.total)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Payments + credits */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div>
              <SectionHeading icon={Banknote}
                title={`Payments · ${doc.payments?.length || 0}`} />
              <Card className="rounded-sm border-zinc-200"
                data-testid="xero-invoice-detail-payments">
                <CardContent className="p-0">
                  {(doc.payments || []).length === 0 ? (
                    <div className="p-4 text-xs italic text-zinc-500 text-center">
                      No payments recorded yet.
                    </div>
                  ) : (
                    doc.payments.map((p, idx) => (
                      <div key={p.payment_id || `p-${idx}`}
                        className="grid grid-cols-[110px_1fr_100px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center">
                        <span className="font-mono text-[11px]">{fmtDate(p.date)}</span>
                        <span className="text-xs truncate">{p.reference || <span className="italic text-zinc-400">No reference</span>}</span>
                        <span className="text-right font-mono font-semibold text-emerald-700">{fmtMoney(p.amount)}</span>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>

            <div>
              <SectionHeading icon={Receipt}
                title={`Credit notes applied · ${doc.credit_notes?.length || 0}`} />
              <Card className="rounded-sm border-zinc-200"
                data-testid="xero-invoice-detail-credits">
                <CardContent className="p-0">
                  {(doc.credit_notes || []).length === 0 ? (
                    <div className="p-4 text-xs italic text-zinc-500 text-center">
                      No credit notes applied to this {isBill ? "bill" : "invoice"}.
                    </div>
                  ) : (
                    doc.credit_notes.map((c, idx) => (
                      <div key={c.credit_note_id || `c-${idx}`}
                        className="grid grid-cols-[110px_1fr_100px] gap-2 px-3 py-2 border-b border-zinc-100 last:border-b-0 text-sm items-center">
                        <span className="font-mono text-[11px]">{fmtDate(c.date)}</span>
                        <span className="text-xs truncate font-mono">{c.credit_note_number || "—"}</span>
                        <span className="text-right font-mono font-semibold text-amber-700">{fmtMoney(c.applied_amount)}</span>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SectionHeading({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-3.5 w-3.5 text-zinc-500" />
      <div className="label-overline text-zinc-700">{title}</div>
    </div>
  );
}

function SummaryBlock({ label, value, sub, accent, icon, testid }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-3 bg-white" data-testid={testid}>
      <div className="label-overline text-[9px]">{label}</div>
      <div className={`font-display font-bold text-base mt-1 truncate ${accent || ""}`}
        title={typeof value === "string" ? value : ""}>
        {value}{icon}
      </div>
      {sub && (
        <div className="text-[10px] text-zinc-500 font-mono mt-0.5 truncate" title={sub}>
          {sub}
        </div>
      )}
    </div>
  );
}

function isOverdue(doc) {
  if (!doc?.due_date) return false;
  if (!["AUTHORISED", "SUBMITTED"].includes(doc.status)) return false;
  if (!(Number(doc.amount_due) > 0)) return false;
  const d = parseXeroDate(doc.due_date);
  return d && d < new Date();
}

function parseXeroDate(s) {
  if (!s) return null;
  const m = String(s).match(/\/Date\((-?\d+)/);
  if (m) {
    const d = new Date(parseInt(m[1], 10));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(String(s).slice(0, 10));
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtDate(s) {
  const d = parseXeroDate(s);
  return d ? d.toISOString().slice(0, 10) : "—";
}

function fmtMoney(n) {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}
